/**
 * The single implementation of Hightouch's custom Liquid filters, shared by the
 * backend SDK engine and the browser bundle (the app's Liquid preview and CEP
 * message rendering). Those runtimes differ only in hashing and error classes,
 * injected as a {@link FilterPlatform}.
 *
 * Add filters here, plus a description in `packages/formkit/src/sdk/functions.ts`.
 */
import atob from "atob";
import btoa from "btoa";
import crypto from "crypto-js";
import * as d3 from "d3-time-format";

export type HashAlgorithm = "sha256" | "sha512" | "md5";

/**
 * `hash` returns a lowercase hex digest; each binding brings its own because the
 * browser cannot resolve Node's built-in crypto. The implementations agree only
 * on well-formed strings — numbers, arrays, byte buffers and unpaired surrogates
 * all differ, and only nil is normalized below.
 *
 * Errors are apollo's on the backend (a resolver keeps its `BAD_USER_INPUT` /
 * `GRAPHQL_VALIDATION_FAILED` code), plain `Error`s in the browser where apollo
 * would pull in Express.
 */
export type FilterPlatform = {
  hash: (algorithm: HashAlgorithm, val: string) => string;
  UserInputError: new (message: string) => Error;
  ValidationError: new (message: string) => Error;
};

const CRYPTO_JS_HASHERS: Record<
  HashAlgorithm,
  (val: string) => crypto.lib.WordArray
> = {
  sha256: (val) => crypto.SHA256(val),
  sha512: (val) => crypto.SHA512(val),
  md5: (val) => crypto.MD5(val),
};

/** The hash both bindings currently pass, defined once so they cannot drift. */
export const cryptoJsHash: FilterPlatform["hash"] = (algorithm, val) =>
  CRYPTO_JS_HASHERS[algorithm](val).toString();

/**
 * LiquidJS's own string filters stringify a nil subject; ours read it directly
 * and threw a TypeError, which rejects the ENTIRE row in `applyMappings` — while
 * the same missing column on a standard mapping is merely omitted. Only
 * null/undefined are special-cased, so every other input is unchanged.
 */
function isNil(val: unknown): val is null | undefined {
  return val === null || val === undefined;
}

/**
 * The hash filters degrade on an empty subject as well as a nil one. Only they
 * need the wider guard: the transform filters above yield `""` on nil, so a
 * chained `{{ missing | b64_encode | sha256 }}` hands the hash an empty string
 * and `sha256("")` is the same phantom identifier a nil subject would have
 * produced, matching every blank-source row together as one user.
 */
function hasNoValue(val: unknown): boolean {
  return isNil(val) || val === "";
}

export function createCommonFilters({
  hash,
  UserInputError,
  ValidationError,
}: FilterPlatform) {
  function sha256(val: string): string {
    // Not hash(""): that digest looks like a real identifier to an ad platform.
    if (hasNoValue(val)) return "";
    return hash("sha256", val);
  }

  function sha512(val: string): string {
    if (hasNoValue(val)) return "";
    return hash("sha512", val);
  }

  function md5(val: string): string {
    if (hasNoValue(val)) return "";
    return hash("md5", val);
  }

  function parse(
    val: string | Record<string, unknown>,
  ): Record<string, unknown> {
    if (typeof val === "object") {
      return val;
    }
    return JSON.parse(val);
  }

  // Object.fromEntries defines own properties; assigning them would let a
  // `__proto__` key trip the inherited setter instead of landing as a key.
  function json_construct(
    val: unknown,
    ...properties: unknown[]
  ): Record<string | number, unknown> {
    let entries: Map<string, unknown>;
    if (typeof val === "object" && !Array.isArray(val) && val !== null) {
      entries = new Map(Object.entries(val));
    } else if (!val) {
      entries = new Map();
    } else {
      throw new UserInputError(
        "Initial argument must be a JSON object or undefined",
      );
    }

    let currentKey;
    for (const [idx, property] of properties.entries()) {
      if (idx % 2 === 0) {
        // this is the property key
        if (typeof property !== "string" && typeof property !== "number") {
          throw new UserInputError("Property key must be a string or number");
        }
        currentKey = String(property); // a Map distinguishes 2 from "2"
      } else {
        // this is the property value
        entries.set(currentKey, property);
      }
    }
    return Object.fromEntries(entries);
  }

  function includes(val: string, substring: string): boolean {
    if (isNil(val)) return false;
    return val.includes(substring);
  }

  function includes_validate(val: string, substring: string): string {
    if (isNil(val))
      throw new ValidationError(
        `Input is missing; expected it to contain ${substring}`,
      );
    if (!val.includes(substring))
      throw new ValidationError(`Input ${val} did not contain ${substring}`);
    return val;
  }

  /**
   * Parse value to date if it's a string.
   *
   * @param value value to be parsed
   * @param format this accepts the similar format from oob of Liquid https://strftime.org/
   * @returns instance of Date representing the parse, returns the original value if unable to parse.
   */
  function parse_date(value: unknown, format: string) {
    if (typeof value !== "string") return value;

    const parse = d3.utcParse(format);
    const result = parse(value);
    if (result instanceof Date) return result;

    // return original value if not parseable.
    return value;
  }

  function to_date(val: unknown, format?: string): unknown {
    if (format) return parse_date(val, format);

    return new Date(val as string | number | Date);
  }

  function to_unix(val: Date | string): number | "" {
    if (isNil(val)) return "";
    let result: number;
    if (typeof val === "string") {
      result = new Date(val).getTime();
    } else {
      result = val.getTime();
    }
    if (isNaN(result)) {
      throw new ValidationError(`Input ${val} is not a valid date`);
    }
    return result;
  }

  function b64_encode(val: string): string {
    if (isNil(val)) return "";
    return btoa(val);
  }

  function b64_decode(val: string): string {
    if (isNil(val)) return "";
    return atob(val);
  }

  function regex_test(val: string, pattern: string, flags?: string): string {
    // Say so, rather than coercing nil into the reason as the word "undefined".
    if (isNil(val))
      throw new ValidationError(`Input is missing; expected a RegEx match`);
    const regEx = RegExp(pattern, flags);
    if (!regEx.test(val))
      throw new ValidationError(`Input ${val} did not contain a RegEx match`);
    return val;
  }

  function regex_replace(
    val: string,
    pattern: string,
    replacement: string,
    flags?: string,
  ): string {
    if (isNil(val)) return "";
    const regEx = RegExp(pattern, flags);
    return val.replace(regEx, replacement);
  }

  function null_if_empty(val: string): string | null {
    return val === "" ? null : val;
  }

  function validate_email(val: string) {
    if (isNil(val))
      throw new ValidationError(
        `Input is missing; expected a RFC 2822 compliant email address`,
      );
    const regEx =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!regEx.test(val))
      throw new ValidationError(
        `Input ${val} is not a RFC 2822 compliant email address`,
      );
    return val;
  }

  function cast(val: string, toType: string): string | number | boolean {
    switch (toType.toLowerCase()) {
      case "string":
        return String(val);
      case "number":
        return Number(val);
      case "boolean":
        return typeof val === "string" && val.toLowerCase() === "false"
          ? false
          : !!val;
      default:
        throw new UserInputError(
          `Type ${toType} is not supported for casting. Supported types are: "string", "number", "boolean"`,
        );
    }
  }

  function push(currArray: unknown[], newValue: unknown): unknown[] {
    if (
      currArray !== null &&
      currArray !== undefined &&
      !Array.isArray(currArray)
    )
      throw new Error(
        `Cannot push item ${newValue} to non-array entity ${currArray}`,
      );
    return [...(currArray || []), newValue];
  }

  function remove_empty(currArray: unknown[]): unknown[] {
    return (currArray || []).filter((item) => !!item);
  }

  // `typeof Object.prototype === "object"`, so deleting in place can strip a
  // member off a shared builtin.
  function remove_key(
    value: Record<string, unknown>,
    keyToRemove: string,
  ): Record<string, unknown> {
    if (typeof value !== "object") {
      throw new ValidationError(`Input ${value} is not an object.`);
    }
    return Object.fromEntries(
      Object.entries(value).filter(([key]) => key !== keyToRemove),
    );
  }

  function array_construct(val: unknown, ...items: unknown[]) {
    let arr: unknown[];
    if (val && Array.isArray(val)) {
      arr = [...val];
    } else if (!val) {
      arr = [];
    } else {
      throw new UserInputError(
        "Initial argument must be an array or undefined",
      );
    }
    arr.push(...items.filter((item) => item != undefined));
    return arr;
  }

  // Keyed by filter name: string-literal keys survive minification, which the
  // frontend registers by. Insertion order is registration order.
  return {
    sha256,
    sha512,
    md5,
    parse,
    json_construct,
    includes,
    includes_validate,
    to_date,
    to_unix,
    b64_encode,
    b64_decode,
    regex_test,
    regex_replace,
    null_if_empty,
    validate_email,
    cast,
    push,
    remove_empty,
    remove_key,
    array_construct,
  };
}

export type CommonFilters = ReturnType<typeof createCommonFilters>;
